/**
 * menuJobRecovery — recuperación de resultados de `processMenuV4` por jobId.
 *
 * Por qué existe: el callable V4 puede tardar 9+ minutos, pero las conexiones HTTP
 * largas mueren antes (NAT/proxy con idle ~5 min, deploys mid-flight). Cuando eso
 * pasa, el `await` del callable lanza un error de transporte aunque el backend siga
 * procesando: el resultado se persiste en Storage bajo el `jobId` que el cliente
 * envió en el payload. Este módulo permite generar ese jobId, clasificar si un error
 * amerita recovery, y hacer polling a `getMenuJobResult` hasta recuperar el resultado.
 *
 * Módulo puro: NO importa firebase — el fetcher se inyecta, lo que lo hace testeable
 * sin mocks de red.
 */

/**
 * Genera un jobId único compatible con el contrato del backend
 * (formato `[A-Za-z0-9_-]{10,64}`).
 *
 * Usa `crypto.randomUUID()` cuando existe (browsers modernos) con fallback a
 * `Math.random()`, más un timestamp base36 que hace el id ordenable en el tiempo
 * y legible en logs. No se reutiliza el `ulid` de `src/utils/featureRollout/`
 * porque es interno de ese feature (acoplamiento innecesario).
 *
 * @returns {string} Id con prefijo `smv4_`, sanitizado a `[A-Za-z0-9_-]`, largo 10-64.
 */
export function generateJobId(): string {
  const uuid =
    typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function'
      ? crypto.randomUUID().replace(/-/g, '')
      : `${Math.random().toString(36).slice(2)}${Math.random().toString(36).slice(2)}`;
  const raw = `smv4_${Date.now().toString(36)}_${uuid}`;
  return raw.replace(/[^A-Za-z0-9_-]/g, '').slice(0, 64);
}

/** Codes de FirebaseError donde el backend pudo haber completado pese al error del cliente. */
const RECOVERABLE_CODES = new Set([
  'functions/deadline-exceeded',
  'functions/internal',
  'functions/unavailable',
  'functions/cancelled',
]);

/** Mensajes (lowercase) de TypeError que los browsers lanzan en fallos de red de fetch. */
const NETWORK_TYPEERROR_PATTERNS = ['failed to fetch', 'networkerror', 'load failed'];

/**
 * Decide si un error del callable es de transporte/timeout (y por lo tanto vale la
 * pena intentar recuperar el resultado por jobId) o de negocio (no hay nada que
 * recuperar: el backend rechazó el request explícitamente).
 *
 * @param {unknown} err Error capturado en el `catch` del callable.
 * @returns {boolean} true si conviene entrar al flujo de recovery por jobId.
 */
export function isRecoverableCallableError(err: unknown): boolean {
  if (typeof err !== 'object' || err === null) {
    return false;
  }
  const code = (err as { code?: unknown }).code;
  if (typeof code === 'string') {
    return RECOVERABLE_CODES.has(code);
  }
  if (err instanceof TypeError) {
    const msg = (err.message || '').toLowerCase();
    return NETWORK_TYPEERROR_PATTERNS.some((pattern) => msg.includes(pattern));
  }
  return false;
}

/** Respuesta del backend para una consulta de job (shape de `getMenuJobResult`). */
export interface JobFetchResponse {
  found: boolean;
  pending?: boolean;
  result?: unknown;
}

/** Opciones de polling; todas tienen default razonable para producción. */
export interface PollOptions {
  /** Espera entre consultas. Default 10s. */
  intervalMs?: number;
  /** Tiempo total máximo de polling. Default 15 min (mismo budget que el callable V4). */
  maxWaitMs?: number;
  /**
   * Tolerancia a `{found:false, pending:false}` desde el inicio del polling.
   * Default 45s — el marker del job pudo no escribirse aún, o el request nunca
   * llegó al backend; pasado este margen asumimos lo segundo.
   */
  graceNotFoundMs?: number;
  /** Inyectable para tests (sleep instantáneo). Default: setTimeout real. */
  sleep?: (ms: number) => Promise<void>;
}

/**
 * Sleep real basado en setTimeout.
 *
 * @param {number} ms Milisegundos a esperar.
 * @returns {Promise<void>} Promesa que resuelve tras la espera.
 */
const defaultSleep = (ms: number): Promise<void> => new Promise((resolve) => setTimeout(resolve, ms));

/**
 * Hace polling del resultado de un job hasta encontrarlo o agotar el budget.
 *
 * Reglas del loop:
 * - `{found:true}` → resuelve con `result` (el payload persistido tal cual).
 * - `{found:false, pending:true}` → el backend sigue procesando; seguir esperando.
 * - `{found:false, pending:false}` → tolerado solo dentro de `graceNotFoundMs`
 *   desde el inicio; pasado el margen lanza `Error('JOB_NOT_FOUND')`.
 * - Errores del fetcher (red intermitente) se toleran y se loggean — la conexión
 *   acaba de demostrar ser inestable, abortar por un blip sería contraproducente.
 * - Budget `maxWaitMs` agotado → lanza `Error('JOB_RECOVERY_TIMEOUT')`.
 *
 * El tiempo transcurrido se mide como max(reloj de pared, suma de sleeps): en
 * producción domina el reloj real (cada fetch puede tardar segundos); en tests con
 * sleep instantáneo domina el tiempo virtual, garantizando terminación.
 *
 * @param {Function} fetchJob Consulta el estado del job (inyectada; sin firebase aquí).
 * @param {string} jobId Id del job a recuperar.
 * @param {PollOptions} [opts] Overrides de intervalos y sleep inyectable.
 * @returns {Promise<unknown>} El `result` persistido por el backend.
 */
export async function pollJobResult(
  fetchJob: (id: string) => Promise<JobFetchResponse>,
  jobId: string,
  opts: PollOptions = {},
): Promise<unknown> {
  const intervalMs = opts.intervalMs ?? 10_000;
  const maxWaitMs = opts.maxWaitMs ?? 15 * 60_000;
  const graceNotFoundMs = opts.graceNotFoundMs ?? 45_000;
  const sleep = opts.sleep ?? defaultSleep;

  const startedAt = Date.now();
  let virtualElapsedMs = 0;
  const elapsedMs = (): number => Math.max(Date.now() - startedAt, virtualElapsedMs);

  while (elapsedMs() < maxWaitMs) {
    let response: JobFetchResponse | null = null;
    try {
      response = await fetchJob(jobId);
    } catch (err) {
      // eslint-disable-next-line no-console
      console.warn(`[menuJobRecovery] Error transitorio consultando job ${jobId}; se reintenta:`, err);
    }

    if (response) {
      if (response.found) {
        return response.result;
      }
      // Solo un `pending === false` EXPLÍCITO cuenta como "job inexistente".
      // Un `pending` undefined (shape inesperado/transitorio) es ambiguo y se
      // sigue polleando — maxWaitMs acota igual (Seer 3393734170).
      if (response.pending === false && elapsedMs() > graceNotFoundMs) {
        throw new Error('JOB_NOT_FOUND');
      }
    }

    await sleep(intervalMs);
    virtualElapsedMs += intervalMs;
  }

  throw new Error('JOB_RECOVERY_TIMEOUT');
}
